import React from 'react';
import classNames from 'classnames';
import { Card } from 'react-bootstrap';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import FormattedCurrency from '../../../../../components/currency/FormattedCurrency';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { computeResults } from '../logic';
import { CompensationReviewItem } from '../types';

type Props = {
  items: CompensationReviewItem[];
  currency: string;
};

// Results panel: reference vs new totals and the net change, all normalised to
// monthly equivalents (mixed pay frequencies are converted to a monthly figure).
export const CompensationReviewResults: React.FC<Props> = ({
  items,
  currency,
}) => {
  const t = useNamespacedTranslation('compensationReviews.results');
  const {
    totalReferenceMonthly,
    totalNewMonthly,
    netChangeAbsolute,
    netChangePercent,
  } = computeResults(items);

  const hasIncrease = netChangeAbsolute > 0;
  const hasDecrease = netChangeAbsolute < 0;

  return (
    <Card className="h-100">
      <Card.Body>
        <div className="d-flex align-items-center mb-4">
          <FlairIcon icon="trending-up-outline" className="me-2" />
          <h4 className="mb-0">{t('title')}</h4>
        </div>

        <div className="mb-4">
          <div className="text-muted small mb-1">{t('total')}</div>
          <div className="d-flex align-items-center">
            <span className="text-muted">
              <FormattedCurrency
                value={totalReferenceMonthly}
                currency={currency}
              />
            </span>
            <FlairIcon
              icon="arrow-forward-outline"
              className="mx-2 text-muted"
            />
            <span
              className={classNames('compensation-results__value', {
                'text-success': hasIncrease,
                'text-danger': hasDecrease,
              })}>
              <FormattedCurrency value={totalNewMonthly} currency={currency} />
            </span>
          </div>
        </div>

        <div>
          <div className="text-muted small mb-1">{t('netChange')}</div>
          <div
            className={classNames('compensation-results__value', {
              'text-success': hasIncrease,
              'text-danger': hasDecrease,
            })}>
            <FormattedCurrency value={netChangeAbsolute} currency={currency} />
            <span className="ms-2 fs-5">{netChangePercent.toFixed(2)}%</span>
          </div>
        </div>

        <div className="text-muted small mt-3">{t('monthlyEquivalents')}</div>
      </Card.Body>
    </Card>
  );
};
